import { useEffect, useState } from "react";
import { Loader2, Trash2, Minus, Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import {
  acceptErrorMessage,
  alreadyResolved,
  isNetworkError,
  myProfileId,
  ORDER_REMOVED_LABEL,
  qrTableLabel,
  resolvedLabel,
} from "@/lib/qrAccept";
import { acceptedToast, NO_ANSWER_TITLE } from "@/lib/qrAcceptPrint";
import { getPendingOrderId, type QRPendingOrder } from "@/types/qr-orders";
import { useAcceptAndPrint } from "./useAcceptAndPrint";
import type { QRPortalSubmitOrderItem } from "@/types/qr-portal";

interface QRApproveModifyDialogProps {
  order: QRPendingOrder | null;
  onClose: () => void;
  onResolved?: () => void;
}

export function QRApproveModifyDialog({ order, onClose, onResolved }: QRApproveModifyDialogProps) {
  const { toast } = useToast();
  const { acceptAndPrint, isPending } = useAcceptAndPrint();
  const [items, setItems] = useState<QRPortalSubmitOrderItem[]>([]);

  useEffect(() => {
    if (order) setItems(order.items.map((i) => ({ ...i })));
    else setItems([]);
  }, [order]);

  const updateQty = (idx: number, qty: number) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, quantity: Math.max(1, qty) } : it)));
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!order) return;
    if (items.length === 0) {
      toast({ title: "At least one item required", variant: "destructive" });
      return;
    }
    try {
      // Modify & accept: the changed lines go to the kitchen; this person gets the table.
      const { result, printing } = await acceptAndPrint(getPendingOrderId(order), items);
      onClose();
      onResolved?.();
      const accepted = acceptedToast(result.pending_order?.table_number ?? order.table_number, await printing);
      toast({ ...accepted, description: accepted.description ?? "With your changes" });
    } catch (error) {
      const resolvedBy = alreadyResolved(error);
      const gone = error instanceof ApiError && error.status === 404;
      if (resolvedBy || gone) {
        toast({
          title: resolvedBy ? resolvedLabel(resolvedBy, myProfileId()) : ORDER_REMOVED_LABEL,
          description: qrTableLabel(order.table_number),
        });
        onClose();
        onResolved?.();
        return;
      }
      toast({
        title: isNetworkError(error) ? NO_ANSWER_TITLE : "Accept failed",
        description: acceptErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modify and accept</DialogTitle>
          <DialogDescription>
            Edit quantities or remove items, then accept. The items go to the kitchen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between gap-2 p-2 rounded border border-border">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.item_name ?? item.item_id}</p>
                {item.special_instructions && (
                  <p className="text-xs italic text-amber-600">{item.special_instructions}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => updateQty(idx, item.quantity - 1)}
                  disabled={item.quantity <= 1}
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => updateQty(idx, item.quantity + 1)}
                >
                  <Plus className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive"
                  onClick={() => removeItem(idx)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isPending || items.length === 0}>
            {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
